/**
 * After a deploy, a tab still on the previous build asks for chunks that no longer exist, and every
 * dynamic import fails ("Failed to fetch dynamically imported module"). Vite announces each failure
 * with a `vite:preloadError` event on window; reloading fetches the new build.
 *
 * This covers every dynamic import, including those `lazyWithRetry` (route pages) does not wrap.
 * Both may ask for a reload on the same failure: that is one navigation, not two. The guard keeps a
 * build that is really broken from reloading in a loop: one reload per {@link RELOAD_WINDOW_MS}, after
 * which the error goes on to `lazyWithRetry` and `ChunkErrorBoundary` as before. A later deploy, in
 * the same tab, gets its own reload.
 */

const RELOAD_KEY = 'vite-preload-error-reload-at';
export const RELOAD_WINDOW_MS = 60_000;

interface ReloadTarget {
  addEventListener(type: 'vite:preloadError', listener: () => void): void;
  location: { reload(): void };
  navigator: { onLine: boolean };
  sessionStorage: Pick<Storage, 'getItem' | 'setItem'>;
}

/** Whether the tab may reload now, recording the reload when it may. Storage failures never throw. */
export function claimReload(storage: Pick<Storage, 'getItem' | 'setItem'>, now: number): boolean {
  try {
    const last = Number(storage.getItem(RELOAD_KEY));
    if (Number.isFinite(last) && last > 0 && now - last < RELOAD_WINDOW_MS) return false;
    storage.setItem(RELOAD_KEY, String(now));
    return true;
  } catch {
    // Storage blocked (private mode, quota): no guard, so no reload; the boundaries take over.
    return false;
  }
}

/**
 * Reloads the page once when Vite fails to load a chunk of the deployed build. A chunk also fails
 * while the network is down (a Mac waking from sleep): reloading then would lose the page, and a
 * draft with it, for an offline error screen. Offline, the failure goes to the boundaries instead.
 */
export function installPreloadErrorReload(target: ReloadTarget = window): void {
  target.addEventListener('vite:preloadError', () => {
    if (target.navigator.onLine === false) return;
    if (claimReload(target.sessionStorage, Date.now())) target.location.reload();
  });
}
